import { useCallback, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { setDesignToolbarSuppressed } from '@/features/editor/lib/designToolbarSuppressed'
import { useAppSelector } from '@/store/hooks'
import type { CourseWithoutBlocks } from '@/types/course'
import { BlockPanel } from './BlockPanel'
import { PANEL_W_CLASS } from './layout'
import { useSheetSwipe } from './useSheetSwipe'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
  /** Уже `lg`: выехала ли панель (кнопка «Настройки экрана» на холсте). */
  sheetOpen: boolean
  onCloseSheet: () => void
}

/**
 * Панель экрана. С `lg` — карточка справа поверх холста, всегда видна при выборе.
 * Уже `lg` (телефон, планшет) экран урока — главное и занимает всю высоту, а панель
 * выезжает справа поверх только по кнопке на холсте: большую часть правок делают
 * прямо на экране. Смонтирована всегда (при выборе) — черновик и фокус не теряются
 * от того, что её спрятали.
 */
export const BlockPanelSheet = ({ course, sheetOpen, onCloseSheet }: Props) => {
  const m = useMessages(messages)
  // Заголовок по содержимому: без выбранного экрана панель показывает урок.
  const isLesson = useAppSelector((s) => s.editor.selectedBlockId === null)
  const closeRef = useRef<HTMLButtonElement>(null)
  // Обработчик — стрелка родителя, новая на каждом рендере: эффект по нему
  // перезапускался бы и каждый раз уводил фокус из поля панели на крестик.
  const onCloseRef = useRef(onCloseSheet)
  useEffect(() => {
    onCloseRef.current = onCloseSheet
  })
  const closeIfOpen = useCallback(() => onCloseRef.current(), [])
  const { sheetRef, handlers: swipeHandlers } = useSheetSwipe(closeIfOpen)
  useEffect(() => {
    if (!sheetOpen) return
    // Панель выехала поверх экрана — фокус в неё, иначе Tab ходит по холсту под ней.
    closeRef.current?.focus()
    // Быстрая панель текста живёт, пока элемент выделен, и перекрывала бы контент шторки.
    setDesignToolbarSuppressed(true)
    const onKeyDown = (e: KeyboardEvent) => {
      // Esc, уже съеденный выпадашкой Radix внутри панели (`preventDefault`), — её.
      if (e.key === 'Escape' && !e.defaultPrevented) onCloseRef.current()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      setDesignToolbarSuppressed(false)
    }
  }, [sheetOpen])
  return (
    <>
      {sheetOpen ? (
        <div
          aria-hidden="true"
          onClick={onCloseSheet}
          className="fixed inset-0 z-40 bg-background/60 backdrop-blur-[2px] duration-200 animate-in fade-in lg:hidden"
        />
      ) : null}
      <div
        ref={sheetRef}
        className={cn(
          // Уже `lg` — нижняя шторка во всю ширину экрана (как окно покупки), с ручкой;
          // с `lg` — карточка справа поверх холста, как была.
          'fixed inset-x-0 bottom-[var(--keyboard-inset,0px)] z-50 max-h-[calc(85dvh-var(--keyboard-inset,0px))] min-h-0 flex-col overflow-hidden rounded-t-3xl bg-surface pb-[env(safe-area-inset-bottom,0px)] shadow-2xl duration-300 animate-in slide-in-from-bottom',
          sheetOpen ? 'flex' : 'hidden',
          'lg:absolute lg:inset-x-auto lg:bottom-4 lg:right-2 lg:top-2 lg:z-10 lg:max-h-none lg:flex lg:rounded-[28px] lg:pb-0 lg:shadow-none lg:slide-in-from-bottom-0 lg:slide-in-from-right',
          PANEL_W_CLASS,
        )}
      >
        {/* Своя строка над панелью: в углу шапки панели уже стоит «Удалить». Фон — тот
            же `surface`, что у панели на ПК: серая карточка с тёмной шапкой читалась
            как два разных блока. */}
        {/* Шапка шторки — ручка: за неё шторку смахивают вниз. */}
        <div className="shrink-0 touch-none lg:hidden" {...swipeHandlers}>
          <div
            aria-hidden="true"
            className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-foreground/20"
          />
          <div className="flex h-11 shrink-0 items-center justify-between pl-4 pr-2">
            <span className="text-sm font-medium text-muted-foreground">
              {isLesson ? m.lessonSettings : m.openBlockPanel}
            </span>
            <button
              ref={closeRef}
              type="button"
              aria-label={m.closeBlockPanel}
              onClick={onCloseSheet}
              className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>
        {/* Масштаб содержимого — вместе с окном, а не только ширина: в низком окне
            (`short`) кегли, иконки и отступы «как на большом экране» выглядели
            крупно, на большом мониторе (`big`) — мелко. `zoom` держит пропорции
            панели разом, без правки каждого поля. */}
        <div className="flex min-h-0 flex-1 flex-col short:lg:[zoom:0.88] big:[zoom:1.12]">
          <BlockPanel course={course} onEmpty={closeIfOpen} />
        </div>
      </div>
    </>
  )
}
